import { useCallback } from 'react';
import { isTourDone, resolveTourStartStep, type CompletedTours, type TourId } from '@sorrem/shared';
import { useAccountSetting } from './use-account-setting';

const NO_COMPLETED_TOURS: CompletedTours = {};

export interface TourProgressState {
    /** Whether the account's settings have loaded; until then the tour neither shows nor saves. */
    isLoaded: boolean;
    /** Finished or skipped at this version (or later). */
    isDone: boolean;
    /** The step to start at: where it was left, else 0. */
    startStep: number;
    /** Remembers the step the player is on, so leaving mid-tour resumes there. */
    saveStep: (step: number) => void;
    /** Marks it done for good: finished, or skipped. */
    finish: () => void;
}

/** One guided tour's progress (#234), from the account's `completedTours` setting. */
export function useTourProgress(tourId: TourId, version: number): TourProgressState {
    const { value: tours, isLoaded, set } = useAccountSetting('completedTours', NO_COMPLETED_TOURS);

    const saveStep = useCallback((step: number): void => {
        set((current) => {
            const progress = current[tourId];
            return progress?.version === version && progress.step === step ? current : { ...current, [tourId]: { version, step } };
        });
    }, [set, tourId, version]);

    const finish = useCallback((): void => {
        set((current) => (isTourDone(current, tourId, version) ? current : { ...current, [tourId]: { version, step: null } }));
    }, [set, tourId, version]);

    return {
        isLoaded,
        isDone: isTourDone(tours, tourId, version),
        startStep: resolveTourStartStep(tours, tourId, version),
        saveStep,
        finish,
    };
}

export interface ReplayToursState {
    /** Whether any tour has been started, finished or skipped. */
    hasProgress: boolean;
    /** Forgets every tour's progress, so each shows again where it first would. */
    replay: () => void;
}

/** The settings action that brings back every guided tour. */
export function useReplayTours(): ReplayToursState {
    const { value: tours, set } = useAccountSetting('completedTours', NO_COMPLETED_TOURS);
    const replay = useCallback((): void => {
        set((current) => (Object.keys(current).length === 0 ? current : {}));
    }, [set]);
    return { hasProgress: Object.keys(tours).length > 0, replay };
}
